import { useState, useCallback } from 'react';
import { toastifyService } from '@services/ToastifyService';
import { SavedLayout } from '../hooks/useLayoutStorage';
import { Dashboard } from '@shared/protos/v2/dashboard';

export const useLayoutSaveLoad = (currentDashboard: Dashboard | undefined, updateDashboard: (dashboard: Dashboard) => Promise<Dashboard>) => {
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<SavedLayout | null>(null);

  const handleSaveLayout = useCallback((savedLayout: SavedLayout) => {
    setIsSaveDialogOpen(false);
    toastifyService.showSuccess(`Layout "${savedLayout.name}" saved successfully!`);
  }, []);

  const handleLoadLayout = useCallback((layout: SavedLayout) => {
    setSelectedLayout(layout);
    setIsLoadDialogOpen(false);
    setIsConfirmDialogOpen(true);
  }, []);

  const handleConfirmLoad = useCallback(async () => {
    if (selectedLayout && currentDashboard) {
      try {
        const updatedDashboard: Dashboard = {
          ...selectedLayout.dashboard,
          id: currentDashboard.id,
          name: currentDashboard.name,
        };

        await updateDashboard(updatedDashboard);
        toastifyService.showSuccess(`Layout "${selectedLayout.name}" loaded successfully!`);
      } catch (error) {
        console.error('Error loading layout:', error);
        toastifyService.showError('Failed to load layout. Please try again.');
      }
    }
    setIsConfirmDialogOpen(false);
    setSelectedLayout(null);
  }, [selectedLayout, currentDashboard, updateDashboard]);

  const handleCancelLoad = useCallback(() => {
    setIsConfirmDialogOpen(false);
    setSelectedLayout(null);
  }, []);

  return {
    isSaveDialogOpen,
    isLoadDialogOpen,
    isConfirmDialogOpen,
    selectedLayout,
    openSaveDialog: () => setIsSaveDialogOpen(true),
    openLoadDialog: () => setIsLoadDialogOpen(true),
    closeSaveDialog: () => setIsSaveDialogOpen(false),
    closeLoadDialog: () => setIsLoadDialogOpen(false),
    handleSaveLayout,
    handleLoadLayout,
    handleConfirmLoad,
    handleCancelLoad,
  };
};
